/**
 * Claims — show view on the shared Inspector grammar (design-system §5).
 *
 * Composed from the lower-level Inspector pieces (not the `ShowPage` template)
 * because the Claim's defining surface is the **evidence ledger** — a custom
 * Relations subsection grouping supporting/contradicting evidence by polarity ×
 * weight ({@link EvidenceLedger}, coverage §2). Header carries the **evaluate**
 * primary action (the verifier agent, `[PROPOSED]`). Body shows the statement.
 */
import { useShow, useGo, useNavigation, type HttpError } from "@refinedev/core";
import { Box, Package, Pencil } from "@/components/ui/icon";
import type { Claim } from "@aos/shared";

import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
  type InspectorAction,
} from "@/components/inspector";
import { humanize } from "@/lib/format";
import { Markdown } from "@/components/app/MarkdownView";
import { formatDateTime } from "../_shared/runs-ui";

import { EvidenceLedger } from "./EvidenceLedger";
import { EvaluateClaimAction } from "./EvaluateClaimAction";

export function ClaimShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { query } = useShow<Claim, HttpError>({ resource: "claims" });
  const { data, isLoading, isError, error, refetch } = query;
  const claim = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !claim) {
    return (
      <ShowPageError
        message={error?.message ?? "Claim not found."}
        onRetry={() => void refetch()}
      />
    );
  }

  const menuActions: InspectorAction[] = [
    {
      label: "Edit",
      icon: Pencil,
      onClick: () => go({ to: editUrl("claims", claim.id) }),
    },
  ];

  return (
    <Inspector>
      <InspectorHeader
        title={claim.title}
        subtitle="An assertion evidence is gathered for or against — its statement and ledger."
        status={claim.status}
        primaryAction={<EvaluateClaimAction claimId={claim.id} />}
        menuActions={menuActions}
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Claim type">{humanize(claim.claim_type)}</Field>
          <Field label="Confidence">
            {claim.confidence_score != null
              ? claim.confidence_score.toFixed(2)
              : null}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Statement">
        <Markdown source={claim.statement} />
      </InspectorSection>

      <InspectorSection title="Evidence ledger">
        <EvidenceLedger claimId={claim.id} />
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "Subject",
              items: [
                {
                  label: "Artifact",
                  icon: Box,
                  resource: "artifacts",
                  id: claim.artifact_id,
                },
                {
                  label: "Package",
                  icon: Package,
                  resource: "packages",
                  id: claim.package_id,
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={claim.id} />
          </Field>
          <Field label="Created">{formatDateTime(claim.created_at)}</Field>
          <Field label="Updated">{formatDateTime(claim.updated_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="claims" entityId={claim.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default ClaimShow;
